import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { IconButton, Table, TBody, Td, THead, Th, Tr } from "@vd/ui";
import { Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { errorMessage } from "../../../api/client";
import {
  audioType,
  type BlockedNumber,
  type ForwardCallerId,
  type ForwardMode,
  type Greeting,
  type GreetingSet,
  type GreetingSlot,
  type NumberGreetings,
  type RepRouting,
  RING_MAX,
  RING_MIN,
  type StatusNow,
  toE164,
  type VoicemailSettings,
  vmBlockedQuery,
  vmKeys,
  vmNumbersQuery,
  vmRepsQuery,
  vmSettingsQuery,
  voicemail,
} from "../../../api/voicemail";
import { requireAdmin, useCanEdit } from "../../../auth/useMe";
import { toast } from "../../../components/toast";
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  HelpTip,
  Input,
  Loading,
  PageHeader,
  Segmented,
  Select,
  Switch,
  Textarea,
  type Tone,
} from "../../../components/ui";
import { fmtDate, fmtDuration, fmtPhone } from "../../../lib/format";

export const Route = createFileRoute("/admin/phone-system/voicemail")({
  staticData: { crumb: { label: "Voicemail", parents: [] } },
  beforeLoad: requireAdmin,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.prefetchQuery(vmSettingsQuery),
      context.queryClient.prefetchQuery(vmNumbersQuery),
      context.queryClient.prefetchQuery(vmRepsQuery),
      context.queryClient.prefetchQuery(vmBlockedQuery),
    ]),
  component: VoicemailPage,
});

function VoicemailPage() {
  return (
    <>
      <PageHeader title="Voicemail" />
      <div className="flex flex-col gap-4">
        <DefaultsCard />
        <SharedNumbersCard />
        <RepsCard />
        <BlockedCard />
      </div>
    </>
  );
}

// Defaults ------------------------------------------------------------------------------------

const MAX_OPTIONS = [30, 60, 120, 180, 300, 600].map((s) => ({
  value: String(s),
  label: s < 60 ? `${s} seconds` : s === 60 ? "1 minute" : `${s / 60} minutes`,
}));

function Row({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <li className="grid items-center gap-x-4 gap-y-2 px-4 py-3 md:grid-cols-[minmax(0,220px)_minmax(0,1fr)]">
      <div className="flex items-center gap-1.5">
        <span className="font-medium text-ui">{label}</span>
        {help ? <HelpTip text={help} /> : null}
      </div>
      <div className="min-w-0">{children}</div>
    </li>
  );
}

function DefaultsCard() {
  const q = useQuery(vmSettingsQuery);
  return (
    <Card title="Defaults">
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <DefaultsForm
          key={JSON.stringify(q.data.settings)}
          settings={q.data.settings}
          defaultGreeting={q.data.default_greeting}
        />
      )}
    </Card>
  );
}

function DefaultsForm({
  settings,
  defaultGreeting,
}: {
  settings: VoicemailSettings;
  defaultGreeting: string;
}) {
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const [ring, setRing] = useState(String(settings.ring_secs));
  const [max, setMax] = useState(String(settings.max_secs));
  const [text, setText] = useState(settings.greeting_text ?? "");
  const [forwarding, setForwarding] = useState(settings.forwarding_enabled);
  const [callerId, setCallerId] = useState<ForwardCallerId>(settings.forward_caller_id);
  const [limit, setLimit] = useState(String(settings.forward_daily_minutes));
  const [zone, setZone] = useState(settings.time_zone);
  const limitNum = Number(limit);
  const limitError =
    limit.trim() === "" || !Number.isInteger(limitNum) || limitNum < 1 || limitNum > 1440
      ? "The daily limit is 1 to 1440 minutes."
      : null;
  const ringNum = Number(ring);
  const ringError =
    ring.trim() === "" || !Number.isInteger(ringNum) || ringNum < RING_MIN || ringNum > RING_MAX
      ? `Ring time is ${RING_MIN} to ${RING_MAX} seconds.`
      : null;
  const next: VoicemailSettings = {
    ring_secs: ringNum,
    max_secs: Number(max),
    greeting_text: text.trim() ? text.trim() : null,
    forwarding_enabled: forwarding,
    forward_caller_id: callerId,
    forward_daily_minutes: limitNum,
    time_zone: zone,
  };
  const dirty = JSON.stringify(next) !== JSON.stringify(settings);
  const save = useMutation({
    mutationFn: () => voicemail.putSettings(next),
    onSuccess: (v) => {
      qc.setQueryData(vmKeys.settings, v);
      toast("Voicemail defaults saved.");
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const reset = () => {
    setRing(String(settings.ring_secs));
    setMax(String(settings.max_secs));
    setText(settings.greeting_text ?? "");
    setForwarding(settings.forwarding_enabled);
    setCallerId(settings.forward_caller_id);
    setLimit(String(settings.forward_daily_minutes));
    setZone(settings.time_zone);
  };

  return (
    <form
      noValidate
      aria-label="Voicemail defaults"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ringError && !limitError && dirty) save.mutate();
      }}
    >
      <ul className="divide-y divide-hairline">
        <Row label="Ring time" help="How long the app rings before the call goes to forwarding or voicemail.">
          <div className="flex items-center gap-2">
            <Input
              aria-label="Ring time in seconds"
              inputMode="numeric"
              className="w-24 tnum"
              value={ring}
              disabled={!canEdit}
              aria-invalid={ringError ? true : undefined}
              onChange={(e) => setRing(e.target.value)}
            />
            <span className="text-ui text-ink-2">seconds</span>
          </div>
          {ringError ? (
            <p role="alert" className="mt-1 text-ui text-danger-ink">
              {ringError}
            </p>
          ) : null}
        </Row>
        <Row label="Longest message">
          <Select
            aria-label="Longest message"
            className="w-48"
            value={max}
            disabled={!canEdit}
            onValueChange={setMax}
            options={MAX_OPTIONS}
          />
        </Row>
        <Row
          label="Default greeting"
          help="Played when a rep has no greeting of their own. {first_name} is the rep's first name and {company} the brand."
        >
          <Textarea
            aria-label="Default greeting"
            rows={2}
            maxLength={500}
            placeholder={defaultGreeting}
            value={text}
            disabled={!canEdit}
            onChange={(e) => setText(e.target.value)}
          />
        </Row>
        <Row label="Time zone" help="Business hours and note times use it for reps who set none.">
          <Select
            aria-label="Time zone"
            className="w-56"
            value={zone}
            disabled={!canEdit}
            onValueChange={setZone}
            options={ZONES.some((z) => z.value === zone) ? ZONES : [...ZONES, { value: zone, label: zone }]}
          />
        </Row>
        <Row
          label="Allow forwarding"
          help="Reps can send their calls to another phone, in the US and Canada. Off until you turn it on."
        >
          <Switch
            label="Allow forwarding"
            checked={forwarding}
            disabled={!canEdit}
            onChange={setForwarding}
          />
        </Row>
        <Row label="Forwarded calls show">
          <Segmented<ForwardCallerId>
            label="Forwarded calls show"
            value={callerId}
            disabled={!canEdit || !forwarding}
            onChange={setCallerId}
            options={[
              { value: "caller", label: "Caller's number" },
              { value: "number", label: "Our number" },
            ]}
          />
        </Row>
        <Row
          label="Daily forwarding limit"
          help="Forwarded minutes each rep may use per day. Past it, calls go to voicemail."
        >
          <div className="flex items-center gap-2">
            <Input
              aria-label="Daily forwarding limit in minutes"
              inputMode="numeric"
              className="w-24 tnum"
              value={limit}
              disabled={!canEdit || !forwarding}
              aria-invalid={limitError ? true : undefined}
              onChange={(e) => setLimit(e.target.value)}
            />
            <span className="text-ui text-ink-2">minutes</span>
          </div>
          {limitError ? (
            <p role="alert" className="mt-1 text-ui text-danger-ink">
              {limitError}
            </p>
          ) : null}
        </Row>
      </ul>
      {canEdit ? (
        <footer className="flex items-center justify-end gap-2 border-hairline border-t px-4 py-3">
          {dirty ? (
            <Button variant="ghost" onClick={reset}>
              Discard changes
            </Button>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            disabled={!dirty || Boolean(ringError) || Boolean(limitError)}
            busy={save.isPending}
          >
            Save
          </Button>
        </footer>
      ) : null}
    </form>
  );
}

// Shared numbers ------------------------------------------------------------------------------

function greetingLabel(g: Greeting | undefined): string {
  if (!g) return "Default";
  if (g.kind === "audio") return `Recorded ${fmtDuration((g.duration_ms ?? 0) / 1000)}`;
  return "Typed";
}

function SharedNumbersCard() {
  const q = useQuery(vmNumbersQuery);
  const [open, setOpen] = useState<string | null>(null);
  const current = q.data?.items.find((n) => n.number_id === open);
  return (
    <Card title="Shared numbers">
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : q.data.items.length === 0 ? (
        <EmptyState
          title="No shared numbers"
          description="Share a number on the Numbers page to give it a greeting."
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <Th>Number</Th>
              <Th>Label</Th>
              <Th numeric>People</Th>
              <Th>Greeting</Th>
              <Th>
                <span className="sr-only">Actions</span>
              </Th>
            </tr>
          </THead>
          <TBody>
            {q.data.items.map((n) => (
              <Tr key={n.number_id}>
                <Td className="tnum">{fmtPhone(n.e164)}</Td>
                <Td>{n.label}</Td>
                <Td numeric>{n.members}</Td>
                <Td>
                  {greetingLabel(n.greetings.standard)}
                  {n.greetings.after_hours ? (
                    <span className="ml-2 text-caption text-ink-3">
                      After hours: {greetingLabel(n.greetings.after_hours).toLowerCase()}
                    </span>
                  ) : null}
                </Td>
                <Td className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setOpen(n.number_id)}>
                    Edit
                  </Button>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {current ? <GreetingsDialog number={current} onClose={() => setOpen(null)} /> : null}
    </Card>
  );
}

function GreetingsDialog({ number, onClose }: { number: NumberGreetings; onClose: () => void }) {
  return (
    <Dialog
      open
      onClose={onClose}
      size="md"
      title={`${number.label || "Shared number"} greetings`}
      description={`What callers to ${fmtPhone(number.e164)} hear when nobody answers.`}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <GreetingCard numberId={number.number_id} slot="standard" title="Greeting" set={number.greetings} />
        <GreetingCard
          numberId={number.number_id}
          slot="after_hours"
          title="After hours"
          set={number.greetings}
        />
      </div>
    </Dialog>
  );
}

function GreetingCard({
  numberId,
  slot,
  title,
  set,
}: {
  numberId: string;
  slot: GreetingSlot;
  title: string;
  set: GreetingSet;
}) {
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const g = set[slot];
  const [typing, setTyping] = useState(false);
  const [text, setText] = useState(g?.kind === "tts" ? (g.text ?? "") : "");
  const [voice, setVoice] = useState(g?.voice ?? set.voices[0]?.id ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const player = usePreview(numberId, slot);

  const run = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: vmKeys.numbers });
      setTyping(false);
      toast(done);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onFile = (file: File | undefined) => {
    if (!file) return;
    const type = audioType(file);
    if (!type) {
      setError("Use an MP3 or WAV file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("The file is larger than 5 MB.");
      return;
    }
    void run(() => voicemail.putAudio(numberId, slot, file, type), "Greeting saved.");
  };

  const now =
    g?.kind === "audio"
      ? `Recorded, ${fmtDuration((g.duration_ms ?? 0) / 1000)}`
      : g?.kind === "tts"
        ? `Typed, ${set.voices.find((v) => v.id === g.voice)?.label ?? "voice"}`
        : slot === "after_hours"
          ? "Same as the greeting"
          : "Default greeting";
  const quote = g?.kind === "tts" ? g.text : !g && slot === "standard" ? set.default_text : null;

  return (
    <section aria-label={title} className="rounded-lg bg-surface-2 p-3 shadow-material">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-medium text-ui">{title}</h3>
          <p className="text-caption text-ink-3">{now}</p>
        </div>
        <div className="flex items-center gap-1">
          {g?.kind === "audio" ? (
            <IconButton
              size="sm"
              label={player.playing ? "Stop" : "Play greeting"}
              icon={player.playing ? <Pause /> : <Play />}
              onClick={player.toggle}
            />
          ) : null}
          {canEdit ? (
            <>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => fileRef.current?.click()}>
                Upload
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setTyping((t) => !t)}>
                Type
              </Button>
              {g ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() =>
                    run(() => voicemail.deleteGreeting(numberId, slot), "Back to the default greeting.")
                  }
                >
                  Use default
                </Button>
              ) : null}
              <input
                ref={fileRef}
                type="file"
                accept="audio/mpeg,audio/wav,.mp3,.wav"
                aria-label={`Upload ${title.toLowerCase()} audio`}
                className="hidden"
                onChange={(e) => {
                  onFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </>
          ) : null}
        </div>
      </div>
      {quote ? <p className="mt-2 text-ui text-ink-2">"{quote}"</p> : null}
      {typing ? (
        <form
          className="mt-3 flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => voicemail.putTts(numberId, slot, text, voice), "Greeting saved.");
          }}
        >
          <Textarea
            aria-label={`${title} text`}
            rows={3}
            maxLength={1000}
            value={text}
            placeholder={set.default_text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="flex items-center justify-between gap-2">
            <Select
              aria-label="Voice"
              className="w-40"
              value={voice}
              onValueChange={setVoice}
              options={set.voices.map((v) => ({ value: v.id, label: v.label }))}
            />
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setTyping(false)}>
                Cancel
              </Button>
              <Button size="sm" type="submit" variant="primary" disabled={!text.trim()} busy={busy}>
                Save
              </Button>
            </div>
          </div>
        </form>
      ) : null}
      {error || player.error ? (
        <p role="alert" className="mt-2 text-ui text-danger-ink">
          {error ?? player.error}
        </p>
      ) : null}
    </section>
  );
}

/** Plays a recorded greeting from a short-lived link. */
function usePreview(numberId: string, slot: GreetingSlot) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => () => audio.current?.pause(), []);
  const toggle = async () => {
    if (playing) {
      audio.current?.pause();
      setPlaying(false);
      return;
    }
    setError(null);
    try {
      const link = await voicemail.greetingAudio(numberId, slot);
      const a = new Audio(link.url);
      audio.current = a;
      a.onended = () => setPlaying(false);
      await a.play();
      setPlaying(true);
    } catch (e) {
      setError(errorMessage(e));
      setPlaying(false);
    }
  };
  return { playing, error, toggle: () => void toggle() };
}

// Reps ----------------------------------------------------------------------------------------

const timeFmt = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });

function statusBadge(s: StatusNow): { label: string; tone: Tone } {
  switch (s.kind) {
    case "dnd":
      return {
        label: s.until ? `Do not disturb until ${timeFmt.format(new Date(s.until))}` : "Do not disturb",
        tone: "warn",
      };
    case "after_hours":
      return { label: "After hours", tone: "neutral" };
    case "forwarding":
      return { label: "Forwarding", tone: "info" };
    default:
      return { label: "Available", tone: "ok" };
  }
}

const WHEN: Record<ForwardMode, string> = {
  off: "",
  always: "always",
  no_answer: "when they don't answer",
  offline: "when the app is closed",
};

const ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Vancouver",
].map((value) => ({ value, label: (value.split("/").pop() ?? value).replace(/_/g, " ") }));

function zoneLabel(tz: string | undefined): string {
  return tz ? (tz.split("/").pop() ?? tz).replace(/_/g, " ") : "company time";
}

function RepsCard() {
  const q = useQuery(vmRepsQuery);
  return (
    <Card title="Reps">
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <Table>
          <THead>
            <tr>
              <Th>Rep</Th>
              <Th>Status</Th>
              <Th>Forwarding</Th>
              <Th>Hours</Th>
              <Th>Greeting</Th>
            </tr>
          </THead>
          <TBody>
            {q.data.items.map((r) => (
              <RepRow key={r.user.id} r={r} />
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}

function RepRow({ r }: { r: RepRouting }) {
  const b = statusBadge(r.status);
  return (
    <Tr>
      <Td>{r.user.name}</Td>
      <Td>
        <Badge tone={b.tone}>{b.label}</Badge>
      </Td>
      <Td className="tnum">
        {r.forward_mode !== "off" && r.forward_to ? (
          <>
            {fmtPhone(r.forward_to)} <span className="text-ink-3">{WHEN[r.forward_mode]}</span>
          </>
        ) : (
          <span className="text-ink-3">Off</span>
        )}
        {r.forward_cap_reached ? (
          <Badge tone="warn" className="ml-2">
            Daily limit reached
          </Badge>
        ) : null}
      </Td>
      <Td>
        {r.hours_enabled ? (
          <>
            On <span className="text-ink-3">{zoneLabel(r.time_zone)}</span>
          </>
        ) : (
          <span className="text-ink-3">Off</span>
        )}
      </Td>
      <Td>{r.custom_greeting ? "Custom" : <span className="text-ink-3">Default</span>}</Td>
    </Tr>
  );
}

// Blocked -------------------------------------------------------------------------------------

function BlockedCard() {
  const q = useQuery(vmBlockedQuery);
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const [raw, setRaw] = useState("");
  const [error, setError] = useState<string | null>(null);
  const block = useMutation({
    mutationFn: (e164: string) => voicemail.block(e164),
    onSuccess: (b) => {
      qc.setQueryData<{ items: BlockedNumber[] }>(vmKeys.blocked, (old) => ({
        items: [b, ...(old?.items ?? []).filter((x) => x.e164 !== b.e164)],
      }));
      setRaw("");
      toast(`${fmtPhone(b.e164)} is blocked for everyone.`);
    },
    onError: (e) => setError(errorMessage(e)),
  });
  const unblock = useMutation({
    mutationFn: (e164: string) => voicemail.unblock(e164),
    onSuccess: (_v, e164) => {
      qc.setQueryData<{ items: BlockedNumber[] }>(vmKeys.blocked, (old) => ({
        items: (old?.items ?? []).filter((x) => x.e164 !== e164),
      }));
      toast(`${fmtPhone(e164)} is unblocked.`);
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const submit = () => {
    const n = toE164(raw);
    if (!n) {
      setError("Enter a full phone number with area code.");
      return;
    }
    setError(null);
    block.mutate(n);
  };

  return (
    <Card title="Blocked for everyone">
      {canEdit ? (
        <form
          className="flex flex-wrap items-start gap-2 border-hairline border-b px-4 py-3"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="flex min-w-0 flex-col">
            <Input
              aria-label="Number to block"
              placeholder="(312) 555-0000"
              className="w-56 tnum"
              value={raw}
              aria-invalid={error ? true : undefined}
              onChange={(e) => {
                setRaw(e.target.value);
                setError(null);
              }}
            />
            {error ? (
              <p role="alert" className="mt-1 text-ui text-danger-ink">
                {error}
              </p>
            ) : null}
          </div>
          <Button type="submit" disabled={!raw.trim()} busy={block.isPending}>
            Block
          </Button>
          <p className="basis-full text-caption text-ink-3">
            Their calls are hung up without ringing and their texts are hidden, for every rep.
          </p>
        </form>
      ) : null}
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : q.data.items.length === 0 ? (
        <EmptyState title="Nobody is blocked" />
      ) : (
        <Table>
          <THead>
            <tr>
              <Th>Number</Th>
              <Th>Contact</Th>
              <Th>Blocked by</Th>
              <Th>When</Th>
              {canEdit ? (
                <Th>
                  <span className="sr-only">Actions</span>
                </Th>
              ) : null}
            </tr>
          </THead>
          <TBody>
            {q.data.items.map((b) => (
              <Tr key={b.e164}>
                <Td className="tnum">{fmtPhone(b.e164)}</Td>
                <Td>{b.contact_name ?? <span className="text-ink-3">Unknown</span>}</Td>
                <Td>{b.created_by?.name ?? ""}</Td>
                <Td className="tnum">{fmtDate(b.created_at)}</Td>
                {canEdit ? (
                  <Td className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Unblock ${fmtPhone(b.e164)}`}
                      disabled={unblock.isPending}
                      onClick={() => unblock.mutate(b.e164)}
                    >
                      Unblock
                    </Button>
                  </Td>
                ) : null}
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
